import { useEffect, useState, type Dispatch, type SetStateAction } from 'react';

import { useEverFailed, useStalled, NO_ANSWER_MS } from '@/hooks/useStalled';
import { listVerdict, type ListVerdict } from '@/lib/listState';
import {
  NO_POLL_TRACKING,
  nextPollInterval,
  trackPollOutcome,
  type PollTracking,
} from '@/lib/pollingBackoff';

/**
 * Le liste QGammon della Hub (Classifica, Partite) con le stesse regole dei pannelli CR — CLAUDE.md
 * del repo, «Gli 8 tab della Hub»: i CINQUE casi di un pannello senza righe, e il polling che non
 * insiste contro un servizio che ha gia' detto di non esserci.
 *
 * Prima di questo hook i due pannelli leggevano `isLoading` / `isError` e basta: tre casi su cinque,
 * e uno spinner senza fine se la richiesta non tornava mai (caso 2 senza la sua scadenza).
 *
 * Due mezzi, perche' il ritmo deve esistere PRIMA della query e il verdetto DOPO:
 *   const ritmo = useQgammonRitmo();
 *   const q = useGetXQuery(undefined, ritmo.opts);
 *   const verdict = useQgammonVerdetto(ritmo, q, righe.length);
 */

/** Una classifica e uno storico cambiano a partita finita: un minuto basta, e il backoff parte da qui. */
export const QGAMMON_LISTA_POLL_MS = 60_000;

export interface QgammonRitmo {
  readonly opts: {
    readonly pollingInterval: number;
    readonly skipPollingIfUnfocused: true;
    readonly refetchOnFocus: true;
    readonly refetchOnMountOrArgChange: true;
  };
  readonly setTracking: Dispatch<SetStateAction<PollTracking>>;
  /**
   * Quanti tentativi di fila sono FALLITI (0 dopo un successo). Si muove solo a tentativo concluso,
   * quindi non lampeggia sotto il polling come `isError` (che torna falso a ogni nuova lettura).
   */
  readonly fallimenti: number;
}

/**
 * `baseMs`: il ritmo a regime. Un minuto per le liste che cambiano a partita finita; la scheda Live
 * passa il suo (`QGAMMON_LIVE_POLL_MS`), perche' li' cambia il punteggio a ogni gioco.
 */
export function useQgammonRitmo(baseMs: number = QGAMMON_LISTA_POLL_MS): QgammonRitmo {
  const [tracking, setTracking] = useState(NO_POLL_TRACKING);
  return {
    opts: {
      // ⛔ Solo un FALLIMENTO rallenta (lib/pollingBackoff.ts): un 200 con lista vuota azzera.
      pollingInterval: nextPollInterval(baseMs, tracking.streak),
      skipPollingIfUnfocused: true,
      // Tornare sulla scheda ritenta SUBITO: il backoff governa solo chi aspetta senza guardare.
      refetchOnFocus: true,
      refetchOnMountOrArgChange: true,
    },
    setTracking,
    fallimenti: tracking.streak,
  };
}

export interface QgammonTentativo {
  readonly data?: unknown;
  readonly isLoading: boolean;
  readonly isError: boolean;
  readonly startedTimeStamp?: number;
  readonly fulfilledTimeStamp?: number;
}

export function useQgammonVerdetto(
  ritmo: QgammonRitmo,
  q: QgammonTentativo,
  rowCount: number,
): ListVerdict {
  const { setTracking } = ritmo;
  const { startedTimeStamp, fulfilledTimeStamp, isError } = q;
  // L'esito si legge dal TENTATIVO (`startedTimeStamp`), mai dallo stato cumulativo: `rejected` non
  // tocca `fulfilledTimeStamp` e non cancella `data`. Idempotente sullo stesso tentativo.
  useEffect(() => {
    setTracking((prev) => trackPollOutcome(prev, { startedTimeStamp, fulfilledTimeStamp, isError }));
  }, [setTracking, startedTimeStamp, fulfilledTimeStamp, isError]);

  // Segnali MONOTONI: `data !== undefined` e `useEverFailed` non lampeggiano sotto il polling.
  const answered = q.data !== undefined;
  const failed = useEverFailed(isError, answered);
  const pending = !answered && !failed;
  const slow = useStalled(pending);
  const noAnswer = useStalled(pending, NO_ANSWER_MS);
  return listVerdict(rowCount, [
    // `loading` resta SOLO `isLoading`: la scadenza di 45 s passa da `failed`, o non scatterebbe mai.
    { reachable: true, loading: q.isLoading, slow, failed: failed || noAnswer, answered },
  ]);
}
